import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Building,
  Calendar,
  ClipboardList,
  Edit,
  MapPin,
  MessageSquare,
  Package,
  Save,
  Target,
  Trash2,
  Users,
  Volume2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { deleteFieldVisitGroup, getFieldVisitGroup, updateFieldVisitGroup } from '../api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { VisitVoiceUpdateDialog } from '../components/VisitVoiceUpdateDialog';
import { ManualFollowUpTaskDialog } from '../components/ManualFollowUpTaskDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const formatDate = (value) => new Date(value).toLocaleDateString('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const formatUpdateTimestamp = (value) => new Date(value).toLocaleString('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
}).replace(' am', ' AM').replace(' pm', ' PM');

const audioSource = (url) => (url?.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);

export default function FieldVisitGroupDetail() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingSummary, setEditingSummary] = useState(searchParams.get('edit') === 'summary');
  const [summaryDraft, setSummaryDraft] = useState('');
  const [savingSummary, setSavingSummary] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    const loadGroup = async () => {
      try {
        setLoading(true);
        const response = await getFieldVisitGroup(groupId);
        setGroup(response.data);
        setSummaryDraft(response.data.visit_summary || '');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to load grouped visit');
        navigate('/field-activity');
      } finally {
        setLoading(false);
      }
    };
    loadGroup();
  }, [groupId, navigate]);

  const startSummaryEdit = () => {
    setSummaryDraft(group.visit_summary || '');
    setEditingSummary(true);
  };

  const cancelSummaryEdit = () => {
    setSummaryDraft(group.visit_summary || '');
    setEditingSummary(false);
    setSearchParams({});
  };

  const saveSummary = async () => {
    setSavingSummary(true);
    try {
      const response = await updateFieldVisitGroup(groupId, { visit_summary: summaryDraft });
      setGroup(response.data);
      setEditingSummary(false);
      setSearchParams({});
      toast.success('Visit summary updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update visit summary');
    } finally {
      setSavingSummary(false);
    }
  };

  const deleteGroup = async () => {
    try {
      await deleteFieldVisitGroup(groupId);
      toast.success('Grouped visit deleted');
      navigate('/field-activity');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete grouped visit');
    }
  };

  const handleBuyerUpdateSaved = (updatedBuyerVisit) => {
    setGroup((current) => ({
      ...current,
      buyer_visits: current.buyer_visits.map((visit) => (
        visit.id === updatedBuyerVisit.id ? updatedBuyerVisit : visit
      )),
    }));
  };

  const handleManualTaskCreated = (buyerVisitId, createdTask) => {
    setGroup((current) => ({
      ...current,
      buyer_visits: current.buyer_visits.map((visit) => (
        visit.id === buyerVisitId
          ? { ...visit, tasks: [createdTask, ...(visit.tasks || [])] }
          : visit
      )),
    }));
  };

  if (loading) {
    return <div className="h-64 flex items-center justify-center" data-testid="visit-group-loading"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-maroon-600" /></div>;
  }
  if (!group) return null;

  return (
    <div className="space-y-4" data-testid="field-visit-group-detail">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex items-start gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/field-activity')} data-testid="back-to-field-activity">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-bold text-maroon-950">Visit {group.visit_code}</h1>
              <Badge className="bg-maroon-100 text-maroon-900 font-mono" data-testid="visit-group-code">{group.visit_code}</Badge>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-500">
              <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{group.geography_market}</span>
              <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{formatDate(group.visit_date)}</span>
              <span className="flex items-center gap-1"><Building className="h-3.5 w-3.5" />{group.buyer_count} buyers</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2 pl-10 sm:pl-0">
          <Button variant="outline" size="sm" onClick={startSummaryEdit} data-testid="edit-group-summary-button">
            <Edit className="mr-1 h-4 w-4" /> Edit Summary
          </Button>
          <Button variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => setDeleteOpen(true)} data-testid="delete-group-button">
            <Trash2 className="mr-1 h-4 w-4" /> Delete
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base lg:text-lg flex items-center gap-2"><MessageSquare className="h-4 w-4 text-maroon-600" />Visit Summary</CardTitle>
        </CardHeader>
        <CardContent>
          {editingSummary ? (
            <div className="space-y-3">
              <Textarea
                value={summaryDraft}
                onChange={(event) => setSummaryDraft(event.target.value)}
                rows={6}
                placeholder="Overall summary for this complete market visit..."
                data-testid="visit-group-summary-input"
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={cancelSummaryEdit} disabled={savingSummary} data-testid="cancel-group-summary-button">
                  <X className="mr-1 h-4 w-4" /> Cancel
                </Button>
                <Button size="sm" onClick={saveSummary} disabled={savingSummary} className="bg-maroon-700 hover:bg-maroon-800" data-testid="save-group-summary-button">
                  <Save className="mr-1 h-4 w-4" />{savingSummary ? 'Saving...' : 'Save Summary'}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm whitespace-pre-wrap text-gray-700" data-testid="visit-group-summary-text">
              {group.visit_summary || 'No visit summary added.'}
            </p>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2 px-1 text-sm text-gray-600" data-testid="visit-group-staff">
        <Users className="h-4 w-4 text-maroon-600" />
        <span className="font-medium">Participants:</span>
        {group.staff_names.length ? group.staff_names.map((name) => <Badge key={name} variant="secondary">{name}</Badge>) : <span>None specified</span>}
      </div>

      <section className="space-y-3" data-testid="visit-group-buyers">
        <h2 className="text-lg font-semibold text-maroon-950">Buyer Details</h2>
        {group.buyer_visits.map((buyerVisit, index) => (
          <div key={buyerVisit.id} className="border border-gray-200 border-l-4 border-l-maroon-400 rounded-md bg-white" data-testid={`group-buyer-${buyerVisit.id}`}>
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
              <div>
                <p className="font-semibold text-maroon-950">{buyerVisit.buyer_name}</p>
                <p className="text-xs text-gray-500">Buyer {index + 1} of {group.buyer_count}</p>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <VisitVoiceUpdateDialog buyerVisit={buyerVisit} onSaved={handleBuyerUpdateSaved} />
                <Button variant="outline" size="sm" className="min-h-[44px] sm:min-h-9" onClick={() => navigate(`/field-activity/edit/${buyerVisit.id}`)} data-testid={`edit-group-buyer-${buyerVisit.id}`}>
                  <Edit className="mr-1 h-3.5 w-3.5" /> Edit Buyer
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
              <div className="space-y-3">
                <div>
                  <p className="mb-1 flex items-center gap-1 text-xs font-semibold text-gray-500"><MessageSquare className="h-3.5 w-3.5" />Discussion</p>
                  <p className="text-sm whitespace-pre-wrap">{buyerVisit.discussion_notes || 'No discussion notes.'}</p>
                </div>
                <div>
                  <p className="mb-1 flex items-center gap-1 text-xs font-semibold text-gray-500"><Package className="h-3.5 w-3.5" />Samples Shown</p>
                  <p className="text-sm whitespace-pre-wrap">{buyerVisit.samples_shown || 'No samples recorded.'}</p>
                </div>
                {buyerVisit.audio_url && (
                  <div>
                    <p className="mb-1 flex items-center gap-1 text-xs font-semibold text-gray-500"><Volume2 className="h-3.5 w-3.5" />Original visit recording · {formatDate(group.visit_date)}</p>
                    <audio controls className="w-full max-w-md" src={audioSource(buyerVisit.audio_url)} data-testid={`group-buyer-audio-${buyerVisit.id}`} />
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <p className="mb-1 flex items-center gap-1 text-xs font-semibold text-gray-500"><Target className="h-3.5 w-3.5" />Potential Items</p>
                  {buyerVisit.potential_items.length ? buyerVisit.potential_items.map((item, itemIndex) => (
                    <div key={`${item.item_description}-${itemIndex}`} className="mb-1 rounded bg-gray-50 px-2 py-1.5 text-sm">
                      <span className="font-medium">{item.item_description}</span>
                      {item.notes && <span className="block text-xs text-gray-500">{item.notes}</span>}
                    </div>
                  )) : <p className="text-sm">No potential items.</p>}
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="flex items-center gap-1 text-xs font-semibold text-gray-500"><ClipboardList className="h-3.5 w-3.5" />Follow-up Tasks</p>
                    <ManualFollowUpTaskDialog
                      buyerVisit={buyerVisit}
                      onCreated={(task) => handleManualTaskCreated(buyerVisit.id, task)}
                    />
                  </div>
                  {buyerVisit.tasks.length ? buyerVisit.tasks.map((task) => (
                    <div key={task.id} className="mb-1 flex items-start justify-between gap-2 rounded bg-gray-50 px-2 py-1.5 text-sm" data-testid={`buyer-task-${task.id}`}>
                      <span className="min-w-0">
                        <span className="block">{task.description}</span>
                        <span className="block text-xs text-gray-500">Due {formatDate(task.due_date)}{task.assigned_names?.length ? ` · ${task.assigned_names.join(', ')}` : ''}</span>
                      </span>
                      <Badge variant="secondary" className="shrink-0">{task.status}</Badge>
                    </div>
                  )) : <p className="text-sm">No follow-up tasks.</p>}
                </div>
              </div>
            </div>
            {(buyerVisit.voice_updates || []).length > 0 && (
              <div className="border-t border-gray-100 bg-gray-50/70 px-4 py-4" data-testid={`buyer-update-timeline-${buyerVisit.id}`}>
                <p className="mb-3 text-xs font-semibold uppercase text-gray-500">Later Updates</p>
                <div className="space-y-3">
                  {[...(buyerVisit.voice_updates || [])]
                    .sort((left, right) => new Date(left.created_at) - new Date(right.created_at))
                    .map((update) => (
                      <div key={update.id} className="border-l-2 border-l-emerald-500 pl-3" data-testid={`buyer-voice-update-${update.id}`}>
                        <p className="text-sm font-semibold text-emerald-800" data-testid={`buyer-voice-update-time-${update.id}`}>
                          Added on {formatUpdateTimestamp(update.created_at)}
                        </p>
                        {update.audio_url && (
                          <audio controls className="mt-2 w-full max-w-md" src={audioSource(update.audio_url)} data-testid={`buyer-update-audio-${update.id}`} />
                        )}
                        <div className="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-3 text-sm">
                          {update.discussion_notes && <div><span className="text-xs font-semibold text-gray-500">New discussion</span><p className="whitespace-pre-wrap">{update.discussion_notes}</p></div>}
                          {update.samples_shown && <div><span className="text-xs font-semibold text-gray-500">New samples / items</span><p className="whitespace-pre-wrap">{update.samples_shown}</p></div>}
                          {update.potential_items?.length > 0 && (
                            <div>
                              <span className="text-xs font-semibold text-gray-500">New potential items</span>
                              {update.potential_items.map((item, itemIndex) => <p key={`${item.item_description}-${itemIndex}`}>{item.item_description}{item.notes ? ` — ${item.notes}` : ''}</p>)}
                            </div>
                          )}
                          {update.follow_up_tasks?.length > 0 && (
                            <div>
                              <span className="text-xs font-semibold text-gray-500">Created follow-up tasks</span>
                              {update.follow_up_tasks.map((task) => <p key={task.task_id}>{task.description} · {formatDate(task.due_date)}</p>)}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </section>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete visit {group.visit_code}?</AlertDialogTitle>
            <AlertDialogDescription>This deletes the grouped visit, all buyer details, and associated tasks. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-group">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteGroup} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-group">Delete Visit</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}